import { useCallback, useMemo } from "react";
import { formatValue, asNumber } from "../../lib/kernelFormat.js";
import { sanitizeRenderColor } from "../../lib/renderSafety.js";
import { CanvasSurface, KernelEmpty, resolveCssColor, colorForKey } from "./shared.jsx";

export const kind = "parallel";

// Parallel coordinates. N vertical axes (one per spec.axes entry),
// each independently scaled to its own min/max. Each data row is a
// polyline that crosses all axes. seriesField (optional) colors rows
// into groups.
export function Component({ spec, data, fillHeight = false }) {
    const model = useMemo(() => buildModel(spec, data), [spec, data]);
    const height = Math.max(160, Math.min(420, Number(spec.height) || 240));

    const draw = useCallback((ctx, { width, height: h, root }) => {
        if (!model) return;
        const { axes, rows } = model;
        const muted = resolveCssColor(root, "var(--plot-ink-soft)") || "#888";
        const line = resolveCssColor(root, "var(--line-soft)") || "#333";
        const ink = resolveCssColor(root, "var(--ink)") || "#fff";

        const padL = 56;
        const padR = 56;
        const padT = 14;
        const padB = 22;
        const gx = Math.max(1, width - padL - padR);
        const gy = Math.max(1, h - padT - padB);
        const N = axes.length;
        if (N < 2) return;
        const stepX = N > 1 ? gx / (N - 1) : 0;

        // axes
        ctx.strokeStyle = line;
        ctx.lineWidth = 1;
        for (let i = 0; i < N; i++) {
            const x = padL + i * stepX;
            ctx.beginPath();
            ctx.moveTo(x, padT);
            ctx.lineTo(x, padT + gy);
            ctx.stroke();
        }

        // axis labels + min/max ticks
        ctx.font = "10px ui-monospace, SFMono-Regular, Menlo, monospace";
        ctx.fillStyle = muted;
        for (let i = 0; i < N; i++) {
            const x = padL + i * stepX;
            ctx.textAlign = "center";
            ctx.textBaseline = "bottom";
            ctx.fillStyle = ink;
            ctx.fillText(truncate(String(axes[i].label), 14), x, padT - 2);
            ctx.fillStyle = muted;
            ctx.textBaseline = "alphabetic";
            const top = axes[i].reverse ? axes[i].min : axes[i].max;
            const bot = axes[i].reverse ? axes[i].max : axes[i].min;
            ctx.fillText(formatTick(top, axes[i].format), x, padT + 10);
            ctx.fillText(formatTick(bot, axes[i].format), x, padT + gy);
        }

        // polylines
        for (const row of rows) {
            const c = resolveCssColor(root, row.color);
            ctx.strokeStyle = withAlpha(c, 0.55);
            ctx.lineWidth = 1.4;
            ctx.beginPath();
            for (let i = 0; i < N; i++) {
                const ax = axes[i];
                const v = row.values[i];
                const range = ax.max - ax.min || 1;
                let t = (v - ax.min) / range;
                if (ax.reverse) t = 1 - t;
                const x = padL + i * stepX;
                const y = padT + (1 - t) * gy;
                if (i === 0) ctx.moveTo(x, y);
                else ctx.lineTo(x, y);
            }
            ctx.stroke();
        }
    }, [model]);

    if (!model || !model.rows.length) {
        return <KernelEmpty message={spec.empty || "no rows"} />;
    }

    return (
        <div className={`rounded-[2px] border border-[color:var(--line)] bg-[color:var(--panel-soft)] px-[10px] py-[8px]${fillHeight ? " flex h-full min-h-0 flex-col" : ""}`}>
            <CanvasSurface
                draw={draw}
                deps={[model]}
                height={fillHeight ? null : height}
                className={`w-full bg-[color:var(--plot-bg)] rounded-[2px] border border-[color:var(--line-soft)]${fillHeight ? " flex-1 min-h-0" : ""}`}
            />
            {model.legend.length > 1 ? (
                <div className="mt-[6px] flex flex-wrap gap-x-[12px] gap-y-[2px] text-[10px]">
                    {model.legend.map((s, i) => (
                        <div key={i} className="flex items-center gap-[4px] text-[color:var(--muted)]">
                            <span
                                className="inline-block h-[8px] w-[8px] rounded-[1px]"
                                style={{ background: sanitizeRenderColor(s.color) }}
                            />
                            <span>{s.label}</span>
                        </div>
                    ))}
                </div>
            ) : null}
        </div>
    );
}

function buildModel(spec, data) {
    const arr = Array.isArray(data) ? data : [];
    const axes = Array.isArray(spec.axes) ? spec.axes : [];
    if (!arr.length || axes.length < 2) return null;

    const axisModels = axes.map((ax) => {
        let mn = Infinity, mx = -Infinity;
        for (const row of arr) {
            const v = asNumber(row?.[ax.field]);
            if (v == null) continue;
            if (v < mn) mn = v;
            if (v > mx) mx = v;
        }
        if (mn === Infinity) { mn = 0; mx = 1; }
        if (mn === mx) { mn -= 1; mx += 1; }
        return {
            label: ax.label || ax.field,
            field: ax.field,
            format: ax.format,
            reverse: !!ax.reverse,
            min: mn,
            max: mx,
        };
    });

    const seriesColor = new Map();
    const rows = [];
    for (const row of arr) {
        if (!row) continue;
        const values = axisModels.map((ax) => asNumber(row[ax.field]) ?? ax.min);
        const key = spec.seriesField ? String(row[spec.seriesField] ?? "—") : "value";
        if (!seriesColor.has(key)) {
            seriesColor.set(
                key,
                sanitizeRenderColor(
                    spec.color && !spec.seriesField
                        ? spec.color
                        : colorForKey(key),
                ),
            );
        }
        rows.push({ values, series: key, color: seriesColor.get(key) });
    }
    const legend = [...seriesColor.entries()].map(([label, color]) => ({ label, color }));
    return { axes: axisModels, rows, legend };
}

function truncate(s, n) {
    if (s.length > n) return s.slice(0, n - 1) + "…";
    return s;
}

function formatTick(v, format) {
    if (format) return formatValue(v, format);
    const n = Number(v);
    if (!Number.isFinite(n)) return "";
    if (Math.abs(n) >= 1000) return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
    if (Number.isInteger(n)) return n.toString();
    return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function withAlpha(color, alpha) {
    if (!color) return color;
    if (color.startsWith("rgba(")) {
        return color.replace(/rgba\(([^)]+)\)/, (m, inner) => {
            const parts = inner.split(",").map((s) => s.trim());
            parts[3] = String(alpha);
            return `rgba(${parts.join(", ")})`;
        });
    }
    if (color.startsWith("rgb(")) {
        return color.replace("rgb(", "rgba(").replace(")", `, ${alpha})`);
    }
    if (color.startsWith("#")) {
        const r = parseInt(color.slice(1, 3), 16);
        const g = parseInt(color.slice(3, 5), 16);
        const b = parseInt(color.slice(5, 7), 16);
        if (Number.isFinite(r) && Number.isFinite(g) && Number.isFinite(b)) {
            return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        }
    }
    return color;
}
